import type { ReactNode } from 'react'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import useOfferPropertySurroundings from 'hooks/Offers/useOfferPropertySurroundings'
import { arrayToMap, nonNullable, sortBy, take } from 'lib/array/arrayUtils'

import { formatNearbyPlaceDistance } from './formatNearbyPlaceDistance'
import OfferPropertySurroundingsCategory, {
  type OfferPropertySurroundingsDisplayPlace,
} from './OfferPropertySurroundingsCategory'
import LineUtensilsIcon from 'LuxKit/Icons/line/LineUtensilsIcon'
import LineArchwayIcon from 'LuxKit/Icons/line/LineArchwayIcon'
import LineIslandIcon from 'LuxKit/Icons/line/LineIslandIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { getAirportsInRange } from './offerPropertySurroundingsUtils'

interface Props {
  offerId: string
  latitude: number
  longitude: number
}

const SurroundingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const LoadingPlaceholder = styled(LoadingBox)`
  min-height: ${rem(160)};
  width: 100%;
`

interface SurroundingsCategoryConfig {
  id: string
  title: string
  icon: ReactNode
  places: Array<App.OfferPropertySurroundingsPlace>
}

interface SurroundingsColumnConfig {
  id: string
  sections: Array<SurroundingsDisplayCategoryConfig>
}

interface SurroundingsDisplayCategoryConfig {
  id: string
  title: string
  icon: ReactNode
  places: Array<OfferPropertySurroundingsDisplayPlace>
}

const FULL_COLUMN_PLACE_LIMIT = 10
const SPLIT_COLUMN_PLACE_LIMIT = 4
const LOAD_ROOT_MARGIN = '200px 0px'

function takeClosestPlaces(
  places: Array<App.OfferPropertySurroundingsPlace> | undefined,
  limit: number,
): Array<App.OfferPropertySurroundingsPlace> {
  return take(sortBy(places ?? [], (place) => place.distanceM, 'asc'), limit)
}

function splitColumnSections(
  sections: Array<SurroundingsCategoryConfig>,
): Array<SurroundingsCategoryConfig> {
  const sectionsWithPlaces = sections.filter((section) => section.places.length > 0)
  if (sectionsWithPlaces.length <= 1) {
    return sectionsWithPlaces.map((section) => ({
      ...section,
      places: takeClosestPlaces(section.places, FULL_COLUMN_PLACE_LIMIT),
    }))
  }

  return sectionsWithPlaces.map((section) => ({
    ...section,
    places: takeClosestPlaces(section.places, SPLIT_COLUMN_PLACE_LIMIT),
  }))
}

function mapDisplayPlaces(
  places: Array<App.OfferPropertySurroundingsPlace>,
  intl: ReturnType<typeof useIntl>,
  regionCode: string,
): Array<OfferPropertySurroundingsDisplayPlace> {
  return places.map((place) => ({
    id: place.id,
    name: place.name,
    distanceLabel: formatNearbyPlaceDistance(intl, place.distanceM, regionCode),
  }))
}

function OfferPropertySurroundings({ offerId, latitude, longitude }: Props) {
  const intl = useIntl()
  const { airports, currentRegionCode } = useContext(GeoContext)
  const [inViewRef, hasBeenInView] = useInView({
    rootMargin: LOAD_ROOT_MARGIN,
    triggerOnce: true,
  })
  const { surroundings, error, fetching, hasFetched } = useOfferPropertySurroundings(
    { offerId },
    { enabled: hasBeenInView },
  )

  const airportPlaces = useMemo<Array<App.OfferPropertySurroundingsPlace>>(() => {
    return getAirportsInRange(airports, latitude, longitude)
  }, [airports, latitude, longitude])

  const nearbyHeading = intl.formatMessage({
    id: 'M21F5-PVxs8lD4T2swns',
    defaultMessage: "What's nearby",
    description: 'Category heading for general nearby places listed below the offer page location map',
  })
  const restaurantsHeading = intl.formatMessage({
    id: 'LA6bPh5lLI00eCvEsSny',
    defaultMessage: 'Restaurants & cafes',
    description: 'Category heading for nearby restaurants and cafes listed below the offer page location map',
  })
  const beachesHeading = intl.formatMessage({
    id: 'LdWy-G38kHZjCs7iie7d',
    defaultMessage: 'Beaches in the neighbourhood',
    description: 'Category heading for nearby beaches listed below the offer page location map',
  })
  const attractionsHeading = intl.formatMessage({
    id: '3KEJw14NRmZ_6hUc1yS-',
    defaultMessage: 'Top attractions',
    description: 'Category heading for nearby attractions listed below the offer page location map',
  })
  const airportsHeading = intl.formatMessage({
    id: 'wJwblzTh2ZfAD7NoaaF7',
    defaultMessage: 'Closest airports',
    description: 'Category heading for nearby airports listed below the offer page location map',
  })
  const disclaimer = intl.formatMessage({
    id: 'LHJmXrb3yybqX-ItGjtV',
    defaultMessage: 'Shortest estimated walking or driving distances displayed, actual distances may vary.',
    description: 'Disclaimer shown under nearby places on the offer page location section explaining distance calculation',
  })

  const columns = useMemo<Array<SurroundingsColumnConfig>>(() => {
    const groupsByCategory = arrayToMap(
      surroundings?.groups ?? [],
      (group) => group.category,
      (group) => group.places,
    )

    const nearbyPlaces = takeClosestPlaces(
      groupsByCategory.get('nearby'),
      FULL_COLUMN_PLACE_LIMIT,
    )
    const leftColumn: SurroundingsColumnConfig | undefined = nearbyPlaces.length ?
        {
          id: 'nearby_column',
          sections: [{
            id: 'nearby',
            title: nearbyHeading,
            icon: <LineWalkingIcon />,
            places: mapDisplayPlaces(nearbyPlaces, intl, currentRegionCode),
          }],
        } :
      undefined

    const middleColumnSections: Array<SurroundingsDisplayCategoryConfig> = splitColumnSections([
      {
        id: 'restaurants_cafes',
        title: restaurantsHeading,
        icon: <LineUtensilsIcon />,
        places: groupsByCategory.get('restaurants_cafes') ?? [],
      },
      {
        id: 'top_attractions',
        title: attractionsHeading,
        icon: <LineArchwayIcon />,
        places: groupsByCategory.get('top_attractions') ?? [],
      },
    ]).map((section) => ({
      ...section,
      places: mapDisplayPlaces(section.places, intl, currentRegionCode),
    }))

    const middleColumn: SurroundingsColumnConfig | undefined = middleColumnSections.length ?
        {
          id: 'restaurants_attractions_column',
          sections: middleColumnSections,
        } :
      undefined

    const rightColumnSections: Array<SurroundingsDisplayCategoryConfig> = splitColumnSections(
      [
        {
          id: 'beach',
          title: beachesHeading,
          icon: <LineIslandIcon />,
          places: groupsByCategory.get('beach') ?? [],
        },
        {
          id: 'airport',
          title: airportsHeading,
          icon: <LinePlaneDepartureIcon />,
          places: airportPlaces,
        },
      ],
    ).map((section) => ({
      ...section,
      places: mapDisplayPlaces(section.places, intl, currentRegionCode),
    }))

    const rightColumn: SurroundingsColumnConfig | undefined = rightColumnSections.length ?
        {
          id: 'beaches_airports_column',
          sections: rightColumnSections,
        } :
      undefined

    return nonNullable([leftColumn, middleColumn, rightColumn])
  }, [
    airportsHeading,
    airportPlaces,
    attractionsHeading,
    beachesHeading,
    currentRegionCode,
    intl,
    nearbyHeading,
    restaurantsHeading,
    surroundings?.groups,
  ])

  const hasFailed = !!error && !fetching
  const showLoading = fetching || (!hasFetched && !hasFailed)
  const showContent = !showLoading && columns.length > 0

  if (hasFailed || (!showLoading && hasFetched && !showContent)) {
    return null
  }

  return (
    <Group direction="vertical" gap={20} ref={inViewRef}>
      {showLoading && <LoadingPlaceholder borderRadius="S" />}
      {showContent && <>
        <SurroundingsGrid>
          {columns.map((column) => (
            <Group direction="vertical" gap={32} key={column.id}>
              {column.sections.map((category) => (
                <OfferPropertySurroundingsCategory
                  key={category.id}
                  title={category.title}
                  icon={category.icon}
                  places={category.places}
                />
              ))}
            </Group>
          ))}
        </SurroundingsGrid>
        <Caption variant="medium" colour="neutral-three">
          {disclaimer}
        </Caption>
      </>}
    </Group>
  )
}

export default OfferPropertySurroundings
